import type { Mixpanel, RequestOptions as MixpanelRequestOptions } from 'mixpanel-browser';
import mixpanel from 'mixpanel-browser';
import {
  acquisitionContext,
  clearAcquisitionContext,
  documentFunnelOpening,
  safelyTrack,
} from './funnel-analytics';

import { CHECKOUT_IFRAME_EXPERIMENT, getCheckoutExperimentSuperProp } from './checkout-tracking';

export enum TrackerService {
  Google = 'google',
  Mixpanel = 'mixpanel',
}

export const SOURCE_NAME = 'penzu-next-01';

export interface TrackerEventOptions {
  services?: TrackerService[];
  mixpanelOptions?: MixpanelRequestOptions;
}

export interface PurchaseEventProperties {
  readonly userId?: string | number;
  readonly transactionId?: string;
  readonly pricing?: Record<string, unknown>;
  readonly paymentTotal: number;
  readonly subscriptionPackage?: string;
  readonly plan?: string;
  readonly subscriptionLength?: number;
  readonly promoCode?: string;
  readonly promoCodeDiscount?: number;
}

interface GA4PurchaseItem {
  item_id: string;
  item_name: string;
  item_brand: string;
  item_category: string;
  price: number;
  quantity: number;
  discount?: number;
  coupon?: string;
}

interface GA4PurchaseEventData {
  currency: string;
  value: number;
  transaction_id: string;
  coupon?: string;
  shipping: number;
  tax: number;
  subscription_length?: number;
  items: GA4PurchaseItem[];
}

export function isTrackingEnabled(): boolean {
  return process.env.NODE_ENV === 'production';
}

function querylessUrl(value: string): string {
  return value.replace(/[?#].*$/, '');
}

// White list of events that should be sent to Google Analytics
const GOOGLE_EVENTS: Record<string, boolean> = {
  'Signup Complete': true,
  purchase: true,
};

export default class Tracker {
  private mixpanelClient: Mixpanel | null = null;
  private experimentSuperProps: Record<string, string> = {};

  constructor() {
    safelyTrack(() => {
      if (this.isMixpanelEnabled()) {
        this.mixpanelClient = mixpanel.init(
          process.env.NEXT_PUBLIC_MIXPANEL_TOKEN as string,
          {
            // OAuth callbacks and magic-link pages use query parameters. Remove
            // Mixpanel's automatic full-URL/referrer fields for every event so
            // those values cannot enter analytics through a future call site.
            property_blacklist: ['$current_url', '$referrer', '$initial_referrer'],
          },
          'main',
        );
      }
    });
  }

  trackEvent(
    eventName: string,
    properties?: Record<string, unknown>,
    options: TrackerEventOptions = {},
  ): void {
    const targetServices = options.services ?? [TrackerService.Google, TrackerService.Mixpanel];
    if (targetServices.indexOf(TrackerService.Mixpanel) !== -1) {
      safelyTrack(() => this.sendMixpanelEvent(eventName, properties, options.mixpanelOptions));
    }
    if (targetServices.indexOf(TrackerService.Google) !== -1 && GOOGLE_EVENTS[eventName]) {
      safelyTrack(() => this.sendGoogleEvent(eventName, properties));
    }
  }

  setUser(userId: string | number, props?: Record<string, string>): void {
    this.setMixpanelUser(userId, props);
    safelyTrack(() => this.setGoogleUser(userId));
  }

  resetUser(): void {
    this.resetMixpanelUser();
    clearAcquisitionContext();
    safelyTrack(() => this.resetGoogleUser());
  }

  /** Reconcile stale account identity without changing this visit's acquisition. */
  resetUserIfIdentified(): void {
    safelyTrack(() => {
      if (
        this.mixpanelClient &&
        (this.mixpanelClient.get_property('$user_id') ||
          this.mixpanelClient.get_property('penzu_user_id') ||
          this.mixpanelClient.get_property('__alias') ||
          /^\d+$/.test(this.mixpanelClient.get_distinct_id()))
      ) {
        this.resetMixpanelUser();
        this.resetGoogleUser();
      }
    });
  }

  setUserProps(props: Record<string, string>): void {
    this.setMixpanelUserProps(props);
  }

  setSuperProps(props: Record<string, string>): void {
    // Only browser experiment assignments survive an account identity reset.
    // Never restore user/profile/acquisition properties from the previous account.
    for (const [name, value] of Object.entries(props)) {
      if (name.startsWith('ab_')) this.experimentSuperProps[name] = value;
    }
    this.setMixpanelSuperProps(props);
  }

  trackPurchase(properties: PurchaseEventProperties): void {
    if (!this.isGoogleEnabled() || typeof window.dataLayer === 'undefined') {
      return;
    }

    try {
      const txIdPrefix = properties.userId != null ? String(properties.userId) : 'U';
      const transactionId = properties.transactionId ?? `${txIdPrefix}-${window.crypto.randomUUID()}`;
      const itemIdentifier = properties.subscriptionPackage ?? properties.plan ?? 'unknown';
      const discountAmount = properties.promoCodeDiscount ?? 0;

      const purchaseItem: GA4PurchaseItem = {
        item_id: itemIdentifier,
        item_name: itemIdentifier,
        item_brand: 'Penzu',
        item_category: 'Subscription',
        // paymentTotal is the final price after any discounts
        price: properties.paymentTotal,
        // One prepaid subscription package; price already covers its whole term.
        quantity: 1,
        ...(discountAmount > 0 && { discount: discountAmount }),
        ...(properties.promoCode && { coupon: properties.promoCode }),
      };

      const purchaseEventData: GA4PurchaseEventData = {
        ...properties.pricing,
        currency: 'USD',
        subscription_length: properties.subscriptionLength,
        value: properties.paymentTotal,
        transaction_id: transactionId,
        ...(properties.promoCode && { coupon: properties.promoCode }),
        shipping: 0,
        tax: 0,
        items: [purchaseItem],
      };

      console.log('GA4 -- sending purchase event: ', JSON.stringify(purchaseEventData, null, 2));
      window.dataLayer.push('event', 'purchase', purchaseEventData);
    } catch (error) {
      console.error('GA4 -- Purchase confirmation failed', error);
    }
  }

  isTrackingEnabled(): boolean {
    return isTrackingEnabled();
  }

  private isMixpanelEnabled(): boolean {
    return this.isTrackingEnabled() && process.env.NEXT_PUBLIC_MIXPANEL_TOKEN !== undefined;
  }

  private isGoogleEnabled(): boolean {
    return this.isTrackingEnabled() && process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID !== undefined;
  }

  private sendMixpanelEvent(
    eventName: string,
    properties?: Record<string, unknown>,
    options: MixpanelRequestOptions = {},
  ): void {
    if (!this.isMixpanelEnabled() || !this.mixpanelClient) {
      return;
    }
    // Preserve the live bucket-b conversion tag even with an older legacy host.
    // Explicit parent assignments take precedence when the handoff is available.
    if (window.self !== window.top && window.location.pathname === '/checkout') {
      const assignment = documentFunnelOpening().experiments?.find(
        ({ name }) => name === CHECKOUT_IFRAME_EXPERIMENT.name,
      );
      this.setSuperProps(
        assignment
          ? {
              [`ab_${assignment.name}`]: `${assignment.variant} (v${assignment.version})`,
            }
          : getCheckoutExperimentSuperProp(),
      );
    }
    this.mixpanelClient.track(
      eventName,
      {
        ...acquisitionContext(),
        ...properties,
        // Keep legacy baseline dimensions authoritative. `state_name` is
        // intentionally omitted from those defaults so callers can supply it.
        ...this.defaultMixpanelProperties(),
      },
      options,
    );
  }

  private defaultMixpanelProperties(): Record<string, unknown> {
    return {
      $time: new Date(),
      source_path: window?.location?.pathname,
      referrer: querylessUrl(document?.referrer || ''),
      online: navigator?.onLine,
      source: SOURCE_NAME,
      client_flow: 'penzu-next',
    };
  }

  private sendGoogleEvent(eventName: string, properties?: Record<string, unknown>): void {
    if (!this.isGoogleEnabled()) {
      return;
    }
    window.dataLayer?.push({ event: eventName, ...properties });
  }

  private setMixpanelUser(userId: string | number, props?: Record<string, string>): void {
    if (!this.isMixpanelEnabled() || !this.mixpanelClient) {
      return;
    }
    try {
      const id = String(userId);
      const previousUser =
        this.mixpanelClient.get_property('$user_id') ??
        this.mixpanelClient.get_property('penzu_user_id') ??
        this.mixpanelClient.get_property('__alias') ??
        (/^\d+$/.test(this.mixpanelClient.get_distinct_id())
          ? this.mixpanelClient.get_distinct_id()
          : null);
      if (previousUser != null && String(previousUser) !== id) this.resetUser();
      this.mixpanelClient.unregister('__alias');
      this.mixpanelClient.identify(id);
      this.mixpanelClient.register({ penzu_user_id: id });
    } catch (error) {
      console.error('setMixpanelUser failed: ', error);
    }
    if (props) {
      this.setMixpanelUserProps(props);
    }
  }

  private setMixpanelUserProps(props: Record<string, string>): void {
    if (!this.isMixpanelEnabled() || !this.mixpanelClient) {
      return;
    }
    try {
      this.mixpanelClient.people.set(props);
      this.setMixpanelSuperProps(props);
    } catch (error) {
      console.error('setMixpanelUserProps failed', error);
    }
  }

  private setMixpanelSuperProps(props: Record<string, string>): void {
    if (!this.isMixpanelEnabled() || !this.mixpanelClient) {
      return;
    }
    try {
      this.mixpanelClient.register(props);
    } catch (error) {
      console.error('setMixpanelSuperProps failed', error);
    }
  }

  private resetMixpanelUser(): void {
    if (!this.isMixpanelEnabled() || !this.mixpanelClient) {
      return;
    }
    try {
      this.mixpanelClient.reset();
      this.mixpanelClient.register(this.experimentSuperProps);
    } catch (error) {
      console.error('resetMixpanelUser failed', error);
    }
  }

  private setGoogleUser(userId: string | number): void {
    if (!this.isGoogleEnabled()) {
      return;
    }
    window.dataLayer?.push({ user_id: String(userId) });
  }

  private resetGoogleUser(): void {
    if (!this.isGoogleEnabled()) {
      return;
    }
    window.dataLayer?.push({ user_id: null });
  }
}
